import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Plus, Heart, Scale, HeartHandshake, Users,
  CheckCircle, XCircle, AlertCircle, Edit, Trash2,
  Building, MapPin, Phone, Shield, Link2, Copy, X
} from 'lucide-react';
import { cn } from '@/lib/utils';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const industryIcons = {
  Medical: Heart,
  Legal: Scale,
  AgedCare: HeartHandshake
};

const industryColors = {
  Medical: '#EF4444',
  Legal: '#3B82F6',
  AgedCare: '#10B981'
};

const industryBadgeColors = {
  Medical: 'bg-red-500/20 text-red-400 border-red-500/30',
  Legal: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  AgedCare: 'bg-green-500/20 text-green-400 border-green-500/30'
};

const complianceDefaults = {
  Medical: { name: 'HIPAA', data_retention_days: 2555, require_consent: true, audit_logging: true, pii_masking: true },
  Legal: { name: 'Legal Professional Privilege', data_retention_days: 3650, require_consent: true, audit_logging: true, pii_masking: true },
  AgedCare: { name: 'Aged Care Quality Standards', data_retention_days: 2555, require_consent: true, audit_logging: true, pii_masking: true },
};

const Tenants = () => {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    tenant_id: '',
    name: '',
    industry_type: '',
  });

  // Generate Link Dialog state
  const [isLinkDialogOpen, setIsLinkDialogOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);
  const [generatedLink, setGeneratedLink] = useState(null);
  const [linkFormData, setLinkFormData] = useState({
    custom_instructions: '',
    intake_questions: [''],
    language_hint: 'English',
    expires_in_hours: 24,
    client_name: '',
    client_phone: '',
    client_email: ''
  });

  useEffect(() => {
    fetchTenants();
  }, []);

  const fetchTenants = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/tenants`);
      setTenants(response.data || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching tenants:', err);
      setError('Failed to load tenants');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTenant = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.industry_type) {
      setError('Name and industry are required');
      return;
    }

    const tenantSlug = formData.tenant_id || formData.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    const compliance = complianceDefaults[formData.industry_type] || complianceDefaults.Medical;

    try {
      setIsSubmitting(true);
      setError(null);

      const payload = {
        tenant_id: tenantSlug,
        name: formData.name,
        industry_type: formData.industry_type,
        compliance_protocol: compliance,
      };

      const response = await axios.post(`${API}/tenants`, payload);
      if (response.data) {
        setTenants(prev => [...prev, response.data]);
        setIsCreateDialogOpen(false);
        setFormData({ tenant_id: '', name: '', industry_type: '' });
      }
    } catch (err) {
      console.error('Error creating tenant:', err);
      setError(err.response?.data?.detail || 'Failed to create tenant');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Generate Link Dialog functions
  const openLinkDialog = (tenant) => {
    setSelectedTenant(tenant);
    setLinkFormData({
      template_type: 'standard',
      custom_instructions: '',
      intake_questions: [''],
      language_hint: 'English',
      expires_in_hours: 24,
      client_name: '',
      client_phone: '',
      client_email: ''
    });
    setGeneratedLink(null);
    setIsLinkDialogOpen(true);
  };

  const addIntakeQuestion = () => {
    setLinkFormData(prev => ({
      ...prev,
      intake_questions: [...prev.intake_questions, '']
    }));
  };

  const removeIntakeQuestion = (index) => {
    if (linkFormData.intake_questions.length > 1) {
      setLinkFormData(prev => ({
        ...prev,
        intake_questions: prev.intake_questions.filter((_, i) => i !== index)
      }));
    }
  };

  const updateIntakeQuestion = (index, value) => {
    setLinkFormData(prev => ({
      ...prev,
      intake_questions: prev.intake_questions.map((q, i) => i === index ? value : q)
    }));
  };

  const handleGenerateLink = async () => {
    if (!selectedTenant) return;

    try {
      setIsGeneratingLink(true);
      setError(null);

      const payload = {
        tenant_id: selectedTenant.tenant_id,
        template_type: linkFormData.template_type,
        custom_instructions: linkFormData.custom_instructions,
        intake_questions: linkFormData.intake_questions.filter(q => q.trim()),
        language_hint: linkFormData.language_hint,
        expires_in_hours: linkFormData.expires_in_hours,
        client_name: linkFormData.client_name,
        client_phone: linkFormData.client_phone,
        client_email: linkFormData.client_email
      };

      const response = await axios.post(`${API}/sessions/create`, payload);
      const { token } = response.data;
      const generatedUrl = `${window.location.origin}/intake/${token}`;
      setGeneratedLink(generatedUrl);
    } catch (err) {
      console.error('Error generating link:', err);
      setError(err.response?.data?.detail || 'Failed to generate session link');
    } finally {
      setIsGeneratingLink(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error('Failed to copy:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48 bg-slate-700" />
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-6">
            <div className="space-y-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="flex items-center space-x-4">
                  <Skeleton className="h-10 w-10 rounded-full bg-slate-700" />
                  <Skeleton className="h-4 w-32 bg-slate-700" />
                  <Skeleton className="h-4 w-24 bg-slate-700" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-white">Tenants</h1>
          <p className="text-sm text-slate-400">Manage healthcare, legal, and aged care organizations</p>
        </div>

        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-teal-500 hover:bg-teal-600 text-white" data-testid="create-tenant-button">
              <Plus className="w-4 h-4 mr-2" />
              Add Tenant
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md">
            <DialogHeader>
              <DialogTitle>Create New Tenant</DialogTitle>
            </DialogHeader>

            {error && (
              <Alert className="bg-red-500/10 border-red-500/20 text-red-400">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleCreateTenant} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-white">Organization Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="bg-slate-700 border-slate-600 text-white"
                  placeholder="e.g., Mercy Hospital Sydney"
                  data-testid="tenant-name-input"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label className="text-white">Tenant ID (slug)</Label>
                <Input
                  value={formData.tenant_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, tenant_id: e.target.value }))}
                  className="bg-slate-700 border-slate-600 text-white"
                  placeholder="auto-generated from name"
                  data-testid="tenant-id-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-white">Industry *</Label>
                <Select
                  value={formData.industry_type}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, industry_type: value }))}
                >
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white" data-testid="industry-select">
                    <SelectValue placeholder="Select industry" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-700 border-slate-600">
                    <SelectItem value="Medical" className="text-white hover:bg-slate-600">
                      <div className="flex items-center space-x-2">
                        <Heart className="w-4 h-4 text-red-400" />
                        <span>Medical (HIPAA)</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="Legal" className="text-white hover:bg-slate-600">
                      <div className="flex items-center space-x-2">
                        <Scale className="w-4 h-4 text-blue-400" />
                        <span>Legal (Professional Privilege)</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="AgedCare" className="text-white hover:bg-slate-600">
                      <div className="flex items-center space-x-2">
                        <HeartHandshake className="w-4 h-4 text-green-400" />
                        <span>Aged Care (Quality Standards)</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formData.industry_type && (
                <div className="rounded-lg bg-slate-700/50 p-3 text-sm text-slate-300">
                  <div className="flex items-center space-x-2 mb-1">
                    <Shield className="w-4 h-4 text-teal-400" />
                    <span className="font-medium text-teal-400">Compliance Protocol</span>
                  </div>
                  <p>{complianceDefaults[formData.industry_type]?.name}</p>
                  <p className="text-xs text-slate-400 mt-1">
                    Data retention: {complianceDefaults[formData.industry_type]?.data_retention_days} days • Consent required • Audit logging enabled
                  </p>
                </div>
              )}

              <div className="flex justify-end space-x-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)}
                  className="bg-slate-700 border-slate-600 text-white hover:bg-slate-600">
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}
                  className="bg-teal-500 hover:bg-teal-600 text-white" data-testid="submit-tenant-button">
                  {isSubmitting ? 'Creating...' : 'Create Tenant'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Error */}
      {error && !isCreateDialogOpen && (
        <Alert className="bg-red-500/10 border-red-500/20 text-red-400">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Tenants Table */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center justify-between">
            <span>All Tenants</span>
            <Badge variant="outline" className="border-slate-600 text-slate-300">{tenants.length} total</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {tenants.length === 0 ? (
            <div className="text-center py-12">
              <Building className="w-12 h-12 mx-auto text-slate-400 mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">No tenants yet</h3>
              <p className="text-slate-400 mb-4">Get started by adding your first tenant</p>
              <Button onClick={() => setIsCreateDialogOpen(true)} className="bg-teal-500 hover:bg-teal-600 text-white">
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Tenant
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-slate-700 hover:bg-slate-700/50">
                  <TableHead className="text-slate-300">Tenant</TableHead>
                  <TableHead className="text-slate-300">Industry</TableHead>
                  <TableHead className="text-slate-300">Compliance</TableHead>
                  <TableHead className="text-slate-300">Status</TableHead>
                  <TableHead className="text-slate-300">Created</TableHead>
                  <TableHead className="text-slate-300">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tenants.map((tenant) => {
                  const IndustryIcon = industryIcons[tenant.industry_type] || Building;
                  const complianceName = typeof tenant.compliance_protocol === 'object'
                    ? tenant.compliance_protocol?.name
                    : tenant.compliance_protocol;

                  return (
                    <TableRow key={tenant.id} className="border-slate-700 hover:bg-slate-700/30" data-testid={`tenant-row-${tenant.tenant_id}`}>
                      <TableCell>
                        <div className="flex items-center space-x-3">
                          <div className="flex items-center justify-center w-10 h-10 rounded-lg"
                            style={{ backgroundColor: `${industryColors[tenant.industry_type] || '#6B7280'}20` }}>
                            <IndustryIcon className="w-5 h-5" style={{ color: industryColors[tenant.industry_type] || '#6B7280' }} />
                          </div>
                          <div>
                            <p className="font-medium text-white">{tenant.name}</p>
                            <p className="text-xs text-slate-500">{tenant.tenant_id}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={cn("px-2 py-1", industryBadgeColors[tenant.industry_type] || 'bg-slate-500/20 text-slate-400')}>
                          {tenant.industry_type}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-1">
                          <Shield className="w-3 h-3 text-teal-400" />
                          <span className="text-sm text-slate-300">{complianceName || 'Standard'}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          {tenant.active !== false ? (
                            <><CheckCircle className="w-4 h-4 text-green-400" /><span className="text-sm text-green-400">Active</span></>
                          ) : (
                            <><XCircle className="w-4 h-4 text-red-400" /><span className="text-sm text-red-400">Inactive</span></>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-slate-400">
                          {tenant.created_at ? new Date(tenant.created_at).toLocaleDateString() : '—'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <Button size="sm" variant="outline"
                            onClick={() => openLinkDialog(tenant)}
                            className="bg-teal-500/20 border-teal-500/30 text-teal-400 hover:bg-teal-500/30"
                            data-testid={`generate-link-${tenant.tenant_id}`}>
                            <Link2 className="w-3 h-3 mr-1" />
                            Link
                          </Button>
                          <Button size="sm" variant="outline"
                            className="bg-slate-700 border-slate-600 text-white hover:bg-slate-600"
                            data-testid={`edit-tenant-${tenant.tenant_id}`}>
                            <Edit className="w-3 h-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Generate Link Dialog */}
      <Dialog open={isLinkDialogOpen} onOpenChange={setIsLinkDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <Link2 className="w-5 h-5 text-teal-400" />
              <span>Generate Session Link - {selectedTenant?.name}</span>
            </DialogTitle>
          </DialogHeader>

          {error && (
            <Alert className="bg-red-500/10 border-red-500/20 text-red-400">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {!generatedLink ? (
            <div className="space-y-6">
              <div className="space-y-2">
                <Label className="text-white">Organization Name</Label>
                <Input
                  value={selectedTenant?.name || ''}
                  disabled
                  className="bg-slate-700 border-slate-600 text-slate-400"
                />
              </div>

              {/* Client Identity (optional) — links sessions into a client profile */}
              <div className="space-y-3 p-3 rounded-lg bg-slate-900/40 border border-slate-700">
                <Label className="text-white text-sm">Client Details (optional)</Label>
                <p className="text-xs text-slate-400 -mt-1">
                  Pre-fill to link this session to a client profile. The AI will also confirm/extract details during the call.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <Input
                    value={linkFormData.client_name}
                    onChange={(e) => setLinkFormData(prev => ({ ...prev, client_name: e.target.value }))}
                    className="bg-slate-700 border-slate-600 text-white"
                    placeholder="Full name"
                    data-testid="client-name-input"
                  />
                  <Input
                    value={linkFormData.client_phone}
                    onChange={(e) => setLinkFormData(prev => ({ ...prev, client_phone: e.target.value }))}
                    className="bg-slate-700 border-slate-600 text-white"
                    placeholder="Phone"
                    data-testid="client-phone-input"
                  />
                  <Input
                    value={linkFormData.client_email}
                    onChange={(e) => setLinkFormData(prev => ({ ...prev, client_email: e.target.value }))}
                    className="bg-slate-700 border-slate-600 text-white"
                    placeholder="Email"
                    data-testid="client-email-input"
                  />
                </div>
              </div>

              {/* Template Selection */}
              <div className="space-y-2">
                <Label className="text-white">Intake Template</Label>
                <Select
                  value={linkFormData.template_type}
                  onValueChange={(value) => setLinkFormData(prev => ({ ...prev, template_type: value }))}
                >
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="quick" className="text-white hover:bg-slate-700">
                      <div className="flex flex-col items-start">
                        <span className="font-medium">Quick Intake</span>
                        <span className="text-xs text-slate-400">~5 minutes • Emergency screening</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="standard" className="text-white hover:bg-slate-700">
                      <div className="flex flex-col items-start">
                        <span className="font-medium">Standard Intake</span>
                        <span className="text-xs text-slate-400">~10 minutes • Comprehensive</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="comprehensive" className="text-white hover:bg-slate-700">
                      <div className="flex flex-col items-start">
                        <span className="font-medium">Comprehensive Assessment</span>
                        <span className="text-xs text-slate-400">~15 minutes • Full detailed history</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-400">
                  Choose the intake length based on urgency and detail needed
                </p>
              </div>

              <div className="space-y-2">
                <Label className="text-white">Custom Instructions</Label>
                <Textarea
                  value={linkFormData.custom_instructions}
                  onChange={(e) => setLinkFormData(prev => ({ ...prev, custom_instructions: e.target.value }))}
                  className="bg-slate-700 border-slate-600 text-white min-h-20"
                  placeholder="Special instructions for the AI assistant (e.g., 'Focus on gathering symptoms and medical history')"
                  data-testid="custom-instructions-textarea"
                />
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-white">Intake Questions</Label>
                  <Button
                    type="button"
                    size="sm"
                    onClick={addIntakeQuestion}
                    className="bg-teal-500 hover:bg-teal-600 text-white"
                    data-testid="add-question-button"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Add Question
                  </Button>
                </div>
                
                {linkFormData.intake_questions.map((question, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <Input
                      value={question}
                      onChange={(e) => updateIntakeQuestion(index, e.target.value)}
                      className="bg-slate-700 border-slate-600 text-white flex-1"
                      placeholder={`Question ${index + 1}`}
                      data-testid={`intake-question-${index}`}
                    />
                    {linkFormData.intake_questions.length > 1 && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => removeIntakeQuestion(index)}
                        className="bg-slate-700 border-slate-600 text-white hover:bg-slate-600"
                        data-testid={`remove-question-${index}`}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-white">Language Hint</Label>
                  <Select
                    value={linkFormData.language_hint}
                    onValueChange={(value) => setLinkFormData(prev => ({ ...prev, language_hint: value }))}
                  >
                    <SelectTrigger className="bg-slate-700 border-slate-600 text-white" data-testid="language-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-700 border-slate-600">
                      <SelectItem value="English" className="text-white hover:bg-slate-600">English</SelectItem>
                      <SelectItem value="Spanish" className="text-white hover:bg-slate-600">Spanish</SelectItem>
                      <SelectItem value="Mandarin" className="text-white hover:bg-slate-600">Mandarin</SelectItem>
                      <SelectItem value="Arabic" className="text-white hover:bg-slate-600">Arabic</SelectItem>
                      <SelectItem value="Hindi" className="text-white hover:bg-slate-600">Hindi</SelectItem>
                      <SelectItem value="French" className="text-white hover:bg-slate-600">French</SelectItem>
                      <SelectItem value="German" className="text-white hover:bg-slate-600">German</SelectItem>
                      <SelectItem value="Portuguese" className="text-white hover:bg-slate-600">Portuguese</SelectItem>
                      <SelectItem value="Italian" className="text-white hover:bg-slate-600">Italian</SelectItem>
                      <SelectItem value="Russian" className="text-white hover:bg-slate-600">Russian</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-white">Link Expires In</Label>
                  <Select
                    value={linkFormData.expires_in_hours.toString()}
                    onValueChange={(value) => setLinkFormData(prev => ({ ...prev, expires_in_hours: parseInt(value) }))}
                  >
                    <SelectTrigger className="bg-slate-700 border-slate-600 text-white" data-testid="expiry-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-700 border-slate-600">
                      <SelectItem value="24" className="text-white hover:bg-slate-600">24 hours</SelectItem>
                      <SelectItem value="48" className="text-white hover:bg-slate-600">48 hours</SelectItem>
                      <SelectItem value="72" className="text-white hover:bg-slate-600">72 hours</SelectItem>
                      <SelectItem value="168" className="text-white hover:bg-slate-600">1 week</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex justify-end space-x-2 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setIsLinkDialogOpen(false)}
                  className="bg-slate-700 border-slate-600 text-white hover:bg-slate-600"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleGenerateLink}
                  disabled={isGeneratingLink}
                  className="bg-teal-500 hover:bg-teal-600 text-white" 
                  data-testid="generate-link-submit"
                >
                  {isGeneratingLink ? 'Generating...' : 'Generate Link'}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-slate-700/50 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span className="font-medium text-green-400">Link Generated Successfully!</span>
                </div>
                <p className="text-sm text-slate-300 mb-4">
                  Share this link with your client. It will expire in {linkFormData.expires_in_hours} hours.
                </p>
                <div className="flex items-center space-x-2">
                  <Input
                    value={generatedLink}
                    readOnly
                    className="bg-slate-800 border-slate-600 text-white font-mono text-sm"
                    data-testid="generated-link-input"
                  />
                  <Button
                    onClick={() => copyToClipboard(generatedLink)}
                    className="bg-teal-500 hover:bg-teal-600 text-white"
                    data-testid="copy-link-button"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="flex justify-end">
                <Button
                  onClick={() => setIsLinkDialogOpen(false)}
                  className="bg-teal-500 hover:bg-teal-600 text-white"
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Tenants;
